import { APP_SEARCH_FAILED_MESSAGE } from '@app-state/app.const';
import { Injectable, inject } from '@angular/core';
import { SearchOnlyAviaPackagesRequest, SearchOnlyAviaPackagesResponse } from '@appTypes/api.types';
import { AirportTreeItem } from '@appTypes/redefined-types';
import { convertToFlattenTree, deepClean } from '@core/misc/misc.utils';
import { DataLoadStatusModel, RequestStateModel, StateFormModel } from '@core/store/store.models';
import { RequestStateParams } from '@core/store/store.types';
import { Action, Actions, Selector, State, StateContext, createSelector, ofActionDispatched } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { CatalogueApiService } from '@shared/services/api/catalogue-api.service';
import { isEmpty } from 'lodash-es';
import {
  catchError,
  combineLatest,
  EMPTY,
  expand,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throwError,
  timer
} from 'rxjs';
import { appRefreshCurrencyData } from '@app-state/app.actions';
import { AviaApiService } from '../services/avia-api.service';
import { airportCityIndex } from '../avia-other-airport';
import {
  aviaGetAirports,
  aviaReset,
  aviaResetSearchResult,
  aviaSearchPackages,
  aviaSetResultFilter
} from './avia.actions';
import { decorateAirportTree } from '../airport-tree.utils';
import { cabinClassList } from '../avia-cabin-class';
import { joinAviaClassSearches } from '../avia-class-search.utils';
import { withinJourneyTime } from '../avia-journey-time.utils';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';

/**
 * packages-api#65 R-19 (docs features/website/mandatory-insurance.md): a coded refusal of the search - an error code with the server's own text
 * (DESTINATION_CURRENCY_MISSING ...). When every class failed, the results show it with its Trace ID instead of the generic search error.
 */
const isRefusal = (e: unknown): e is ApiErrorResponse =>
  !!(e as ApiErrorResponse | null)?.errorCode && !!(e as ApiErrorResponse | null)?.displayErrorMessage;

/**
 * The error state of the results: the refusal's text and answer (Trace ID), else the generic search error - R-3 (docs
 * features/website/currency-and-search-failures.md, website-web#150): still with the Trace ID of the failed request when it has one.
 */
const searchErrorState = (e: unknown) =>
  isRefusal(e)
    ? new DataLoadStatusModel('error', e.displayErrorMessage, e)
    : new DataLoadStatusModel(
        'error',
        (e as ApiErrorResponse | null)?.displayErrorMessage ?? APP_SEARCH_FAILED_MESSAGE,
        failedWithTrace(e)
      );
/** R-3: the answer of a failed request, kept only for its Trace ID (`activityId`, set by the interceptor on every error). */
const failedWithTrace = (e: unknown): ApiErrorResponse | undefined => {
  const activityId = (e as ApiErrorResponse | null)?.activityId;
  return activityId ? { activityId } : undefined;
};
/** R-CM6 + R-3: every class failed - the first coded refusal, else the generic text with the Trace ID of a failed class. */
const allClassesFailed = (refusal: ApiErrorResponse | null, failed: ApiErrorResponse | undefined) =>
  refusal ?? { displayErrorMessage: APP_SEARCH_FAILED_MESSAGE, activityId: failed?.activityId };
import { AviaFilterFormModel, AviaPageDataModel, AviaStateModel } from './avia.model';
import { emptySearchResults } from '@shared/components/providers-unavailable/empty-search-results';

const defaults = new AviaStateModel();

@State<AviaStateModel>({
  name: 'avia',
  defaults
})
@Injectable()
export class AviaState {
  readonly #api = inject(AviaApiService);
  readonly #catalougeApi = inject(CatalogueApiService);
  /**
   * website-web#185: a search is no longer cancelled by a second press, so "Clear All" / a state reset ends the running one instead - its late
   * answer or progress poll must not overwrite the results of the search started after the reset.
   */
  readonly #resetSearch$ = inject(Actions).pipe(ofActionDispatched(aviaResetSearchResult, aviaReset));

  @Action(aviaReset)
  hotelsReset({ setState }: StateContext<AviaStateModel>) {
    return setState(defaults);
  }
  // website-web#185 (avia-search-form R-AVF12): no cancelUncompleted - a second press while loading returns on the guard below; cancelling
  // would abort the running search and leave the spinner forever (same fault as excursions, website-web#158)
  @Action(aviaSearchPackages)
  aviaSearchPackages(
    { setState, getState, patchState, dispatch }: StateContext<AviaStateModel>,
    { paging }: aviaSearchPackages
  ) {
    const {
      searchResults: { loadState },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;

    if (!searchForm.model) return;

    const { ticketTypes, ...formModel } = searchForm.model;
    // R-AVF4 (docs#439 point 1): a multi-city search never sends a return date - a hidden one (a search stored before the fix and
    // written back by [ngxsForm] after a reload) would make flights-api search that leg as a round trip
    const model =
      formModel._flightTicketType === 'multipleFlights'
        ? {
            ...formModel,
            flightRequests: formModel.flightRequests?.map((request) => ({ ...request, returnDate: null }))
          }
        : formModel;
    const payload = deepClean(model);
    if (isEmpty(payload)) return;
    // avia-class-multi-select R-CM4 / R-CM5: one request per ticked class (one class = exactly the request of before); a page of an
    // earlier search (pagingId) asks for that search's own class only
    const classes = paging?.pagingId ? [payload.ticketType] : cabinClassList(ticketTypes, payload.ticketType);

    if (!paging?.pagingId)
      patchState({
        searchResults: new RequestStateModel<SearchOnlyAviaPackagesResponse>(new DataLoadStatusModel('loading')),
        searchedTicketTypes: cabinClassList(ticketTypes, payload.ticketType),
        searchedFlightRequests: [...(model.flightRequests ?? [])],
        filterForm: new StateFormModel(new AviaFilterFormModel()) //reset filters
      });
    else
      setState(
        patch<AviaStateModel>({
          searchResults: patch<RequestStateModel<SearchOnlyAviaPackagesResponse>>({
            loadState: new DataLoadStatusModel('loading')
          })
        })
      );

    dispatch(new appRefreshCurrencyData()); // rates every search, the currency list if it never loaded
    if (!paging?.pagingId)
      return this.#progressiveSearch(payload, cabinClassList(ticketTypes, payload.ticketType), patchState).pipe(
        takeUntil(this.#resetSearch$)
      );
    let refusal: ApiErrorResponse | null = null; // R-19: the first coded refusal of a class
    let failed: ApiErrorResponse | undefined; // R-3: a failed class with a Trace ID
    const search$: Observable<SearchOnlyAviaPackagesResponse> =
      classes.length > 1
        ? // R-CM6: a class whose search fails is left out; only when every one fails is the search an error
          forkJoin(
            classes.map((ticketType) =>
              this.#api.searchOnlyAviaPackages({ ...payload, ticketType, ...paging }).pipe(
                catchError((e) => {
                  if (!refusal && isRefusal(e)) refusal = e;
                  failed ??= failedWithTrace(e);
                  return of(null);
                })
              )
            )
          ).pipe(
            map((answers) => {
              const joined = joinAviaClassSearches(answers);
              if (!joined) throw allClassesFailed(refusal, failed);
              return joined;
            })
          )
        : this.#api.searchOnlyAviaPackages({ ...payload, ticketType: classes[0] ?? payload.ticketType, ...paging });
    return search$.pipe(
      tap((response) => {
        patchState({
          searchResults:
            !paging?.pagingId && !response.packages?.length
              ? // R-FU4 (flights-api#3): the empty state keeps the flight providers that did not answer, so the notice is shown there too
                emptySearchResults<SearchOnlyAviaPackagesResponse>(response.providersUnavailable)
              : new RequestStateModel<SearchOnlyAviaPackagesResponse>(response)
        });
      }),
      catchError((e) => {
        if (!paging?.pagingId)
          patchState({
            searchResults: new RequestStateModel<SearchOnlyAviaPackagesResponse>(searchErrorState(e))
          });
        else
          setState(
            patch<AviaStateModel>({
              searchResults: patch<RequestStateModel<SearchOnlyAviaPackagesResponse>>({
                loadState: searchErrorState(e)
              })
            })
          );
        return throwError(() => e);
      }),
      takeUntil(this.#resetSearch$)
    );
  }

  /**
   * docs features/website/flight-search-progressive.md R-PF1 .. R-PF4: one search per ticked class, each asking to be progressive. Where online-api runs it
   * progressively, each answer lists the providers that answered so far and is polled until complete; every class's latest answer is joined into the
   * one list (cheapest first) the moment it changes, so nothing waits for the slowest provider. A classic answer is simply complete (R-PF8). A class
   * whose search fails is left out; only when every one fails is the search an error (R-CM6).
   */
  #progressiveSearch(
    payload: SearchOnlyAviaPackagesRequest,
    classes: string[],
    patchState: StateContext<AviaStateModel>['patchState']
  ) {
    type ClassState = { answer: SearchOnlyAviaPackagesResponse | null; done: boolean };
    const ask = (ticketType: string, progressId?: string | null) =>
      this.#api.searchOnlyAviaPackages({
        ...payload,
        ticketType,
        progressive: true,
        ...(progressId ? { progressId } : {})
      });
    const running = (r: SearchOnlyAviaPackagesResponse | null) => !!r?.progressId && r.searchComplete === false;
    let refusal: ApiErrorResponse | null = null; // R-19: the first coded refusal of a class, shown when every class failed
    let failed: ApiErrorResponse | undefined; // R-3: a failed class with a Trace ID, shown under the generic text
    const one = (ticketType: string): Observable<ClassState> =>
      ask(ticketType).pipe(
        catchError((e) => {
          if (!refusal && isRefusal(e)) refusal = e;
          failed ??= failedWithTrace(e);
          return of(null);
        }),
        expand((r) =>
          running(r)
            ? timer(r!.pollAfterMs ?? 1000).pipe(
                switchMap(() => ask(ticketType, r!.progressId)),
                // a poll that fails ends that class with what it had; a refusal with nothing found ends it as refused (R-19)
                catchError((e) => {
                  if (isRefusal(e) && !r!.packages?.length) {
                    refusal ??= e;
                    return of(null);
                  }
                  return of({ ...r!, searchComplete: true, pollAfterMs: null });
                })
              )
            : EMPTY
        ),
        map((answer) => ({ answer, done: !running(answer) })),
        startWith({ answer: null, done: false } as ClassState)
      );

    return combineLatest(classes.map(one)).pipe(
      map((states) => {
        const live = states.filter((c) => !(c.done && c.answer === null)); // a failed class is left out
        const joined = joinAviaClassSearches(live.map((c) => c.answer));
        const allDone = states.every((c) => c.done);
        return { joined, allDone };
      }),
      // website-web#170, R-PF2 / user manual step 3: a running answer without flights still carries who is being asked
      filter(
        ({ joined, allDone }) =>
          allDone || !!joined?.packages?.length || (!!joined?.providersTotal && joined.searchComplete === false)
      ),
      tap(({ joined, allDone }) => {
        if (!joined) throw allClassesFailed(refusal, failed);
        const response = allDone ? { ...joined, searchComplete: true, pollAfterMs: null } : joined;
        if (!response.packages?.length && !allDone) {
          // no flight yet: the search stays loading (loader + skeleton, never "not found") and the progress box reads the counts
          const stillSearching = new RequestStateModel<SearchOnlyAviaPackagesResponse>(
            new DataLoadStatusModel('loading')
          );
          stillSearching.response = response;
          patchState({ searchResults: stillSearching });
        } else if (!response.packages?.length) {
          // R-FU4: the empty state keeps the providers that did not answer; the supplier summary rides along (dev / test)
          const empty = emptySearchResults<SearchOnlyAviaPackagesResponse>(response.providersUnavailable);
          empty.loadState.additionlData = {
            ...empty.loadState.additionlData,
            diagnostics: response.diagnostics ?? null
          };
          patchState({ searchResults: empty });
        } else patchState({ searchResults: new RequestStateModel<SearchOnlyAviaPackagesResponse>(response) });
      }),
      map(({ joined }) => joined as SearchOnlyAviaPackagesResponse),
      catchError((e) => {
        patchState({
          searchResults: new RequestStateModel<SearchOnlyAviaPackagesResponse>(searchErrorState(e))
        });
        return throwError(() => e);
      })
    );
  }

  /** avia-class-multi-select R-CM3: the classes of the last search - the results' texts follow these, not the live Класс dropdown. */
  @Selector([AviaState])
  static searchedTicketTypes({ searchedTicketTypes }: AviaStateModel) {
    return searchedTicketTypes ?? [];
  }

  /** Q-9 (airport-picker R-3): the places of the last search + the catalogue's airport -> city index the rows' marks read. */
  @Selector([AviaState])
  static otherAirportContext({ searchedFlightRequests, pageData: { airports } }: AviaStateModel) {
    return { requests: searchedFlightRequests ?? [], cities: airportCityIndex(airports?.response) };
  }

  @Action(aviaResetSearchResult)
  aviaResetSearchResult({ patchState }: StateContext<AviaStateModel>) {
    patchState({
      searchResults: new RequestStateModel<SearchOnlyAviaPackagesResponse>()
    });
  }
  @Action(aviaGetAirports, { cancelUncompleted: true })
  aviaGetAirports({ getState, setState }: StateContext<AviaStateModel>, { refresh }: aviaGetAirports) {
    const {
      pageData: { airports }
    } = getState();

    if (airports.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<AirportTreeItem[]>) => {
      if ((stateParams as AirportTreeItem[]).length) {
        const airports_flat = convertToFlattenTree(stateParams as AirportTreeItem[], 'children');
        setState(
          patch<AviaStateModel>({
            pageData: patch<AviaPageDataModel>({ airports_flat })
          })
        );
      }

      setState(
        patch<AviaStateModel>({
          pageData: patch<AviaPageDataModel>({ airports: new RequestStateModel(stateParams) })
        })
      );
    };

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalougeApi.getAirportTree().pipe(
      // airport picker (docs features/website/airport-picker): the rows' flag, counts and city come from `_view`
      tap((response) => updateState(decorateAirportTree(response))),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you"
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(aviaSetResultFilter, { cancelUncompleted: true })
  aviaSetResultFilter({ patchState }: StateContext<AviaStateModel>, { data }: aviaSetResultFilter) {
    patchState({
      filterForm: new StateFormModel(data)
    });
  }
  /* Selectors */

  @Selector([AviaState])
  static airports({ pageData: { airports } }: AviaStateModel) {
    return airports;
  }

  @Selector([AviaState])
  static searchResults({ searchResults, filterForm: { model: filterParams } }: AviaStateModel) {
    if (filterParams && searchResults.response?.packages) {
      let packages = searchResults.response.packages;

      if (filterParams.airlines?.length)
        packages = packages.filter(
          (f) =>
            // website-web#14: an offer stays when the chosen airline flies ANY segment of either leg
            Boolean(
              f.flight?.departureSegments?.some((d) => d?.airlineCode && filterParams.airlines.includes(d.airlineCode))
            ) ||
            Boolean(
              f.flight?.returnSegments?.some((r) => r?.airlineCode && filterParams.airlines.includes(r.airlineCode))
            )
        );
      if (filterParams.stopsType === 'nonstop')
        packages = packages.filter(
          (e) =>
            e?.flight?.departureSegments?.length === 1 &&
            (e?.flight?.returnSegments?.length ? e?.flight?.returnSegments?.length === 1 : true)
        );
      else if (filterParams.stopsType === '1stop')
        packages = packages.filter(
          (e) =>
            e?.flight?.departureSegments?.length === 2 &&
            (e?.flight?.returnSegments?.length ? e?.flight?.returnSegments?.length === 2 : true)
        );

      if (filterParams.maxJourneyHours)
        packages = packages.filter((e) => withinJourneyTime(e, filterParams.maxJourneyHours));

      return { ...searchResults, response: { ...searchResults.response, packages } };
    }
    return searchResults;
  }
  @Selector([AviaState])
  static isResult({ searchResults }: AviaStateModel) {
    return Boolean(searchResults?.response?.packages?.length);
  }

  static findLocationName(code: string) {
    return createSelector([AviaState], ({ pageData: { airports_flat } }: AviaStateModel): string | null => {
      if (!airports_flat?.length) return null;
      return airports_flat?.find((c) => c.value === code)?.label ?? null;
    });
  }

  @Selector([AviaState])
  static searchFormData({ searchForm: { model } }: AviaStateModel) {
    return model;
  }
  @Selector([AviaState])
  static searchLoadStatus({ searchResults: { loadState } }: AviaStateModel): DataLoadStatusModel {
    return loadState;
  }
}
